import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { CdkDragDrop, CDK_DRAG_CONFIG, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, Optional, Output, Self, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { ErrorStateMatcher } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';

import { MatInputBase } from '../input-base';
import { computeLuminance, getRgbComponents } from '../../color.utils';

export interface Suggestion {
  display: string
  tooltip?: string
  value: string
}

const dragConfig = {
  dragStartThreshold: 0,
  pointerDirectionChangeThreshold: 5,
  zIndex: 10000,
};

@Component({
  host: {
    'test-id': 'fw-chip-input',
  },
  selector: 'fw-chip-input',
  styleUrls: ['./fw-chip-input.component.scss'],
  templateUrl: './fw-chip-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatFormFieldControl, useExisting: FwChipInputComponent }, { provide: CDK_DRAG_CONFIG, useValue: dragConfig }],
})
export class FwChipInputComponent extends MatInputBase implements DoCheck, OnDestroy, ControlValueAccessor, MatFormFieldControl<string[]> {
  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(text: string) {
    this._placeholder = text;
    this.stateChanges.next();
  }
  private _placeholder: string

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input() disableDrag = false;

  @Input('aria-describedby') userAriaDescribedBy?: string
  @Input('chipColor') chipColor?: (value: string) => string
  @Input('strictSuggestions') strictSuggestions = false
  @Input('isWithBorder') isWithBorder = false
  @Input('isError') isError = false
  @Input('suggestions') suggestions: Suggestion[] = []
  @Input() validators: ((value: string) => boolean)[] = []
  @Input() isOutlined = false;
  @Input() isOnBlurAddingValue = true;
  @Input() isOnEnterAddingValue = true;
  @Input() allowDuplicates = false;
  @Input() maxOptionWidth: string;

  @Output('focus') focus$ = new Subject<void>()
  @Output('inputChange') inputChange$ = new Subject<string>()

  @ViewChild('autoValue', { static: true }) autoValue: MatAutocomplete
  @ViewChild('chipInput', { static: true }) chipInput: ElementRef

  chips = [];
  filteredSuggestions: Suggestion[] = []
  isFocus = false;

  /** ControlValueAccessor */
  onChange: (value: string[]) => void = () => { } // eslint-disable-line @typescript-eslint/no-empty-function
  onTouched: Function = () => { } // eslint-disable-line @typescript-eslint/no-empty-function
  /**/

  /**
   * MatFormFieldControl
   * https://material.angular.io/guide/creating-a-custom-form-field-control
   */
  static nextId = 0;
  @HostBinding() id = `fw-chip-input-${FwChipInputComponent.nextId++}`;
  controlType = 'fw-chip-input';
  get value(): string[] {
    return Array.from(this.chips);
  }
  set value(value: string[]) {
    this.writeValue(value);
    this.stateChanges.next();
  }
  override stateChanges = new Subject<void>();
  focused = false
  touched = false
  get empty(): boolean {
    return this.chips.length === 0;
  }
  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }
  /**/

  constructor(
    cdr: ChangeDetectorRef,
    defaultErrorStateMatcher: ErrorStateMatcher,
    private elementRef: ElementRef,
    @Self() @Optional() ngControl: NgControl,
    @Optional() parentForm: NgForm,
    @Optional() parentFormGroup: FormGroupDirective,
  ) {
    super(defaultErrorStateMatcher, parentForm, parentFormGroup, ngControl);
    if (ngControl) {
      ngControl.valueAccessor = this;
    }
    // We complete this stream in ngOnDestroy, no cleanup required
    this.stateChanges.subscribe(() => {
      cdr.markForCheck();
    });
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.suggestions?.currentValue) {
      this.filterSuggestions();
    }
  }

  /* ControlValueAccessor */
  writeValue(value: string[]): void {
    if (this.allowDuplicates) {
      this.chips = [...value];
    } else {
      this.chips = Array.from(new Set(value));
      this.stateChanges.next();
    }
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onTouched = fn;
  }

  setDisabledState?(disabled: boolean): void {
    this.disabled = disabled;
  }
  /**/

  /* MatFormFieldControl */
  onFocusIn(event: FocusEvent): void { // eslint-disable-line @typescript-eslint/no-unused-vars
    this.isFocus = true;

    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
      this.focus$.next();
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this.isOnBlurAddingValue) {
      this.chipInput.nativeElement.value = '';
      return;
    }

    this.isFocus = false;

    const target: HTMLElement = event.target as HTMLElement;
    if (!target || target.tagName !== 'MAT-OPTION') {
      this.addChip({
        input: this.chipInput.nativeElement,
        value: this.chipInput.nativeElement.value,
        chipInput: null,
      });
    }

    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement = this.elementRef.nativeElement;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.chipInput.nativeElement.focus();
    }
  }
  /**/

  getChipStyle(value: string): Record<string, string> {
    if (this.chipColor) {
      const backgroundColor = this.chipColor(value);
      const rgbColor = getRgbComponents(backgroundColor);
      const color = computeLuminance(...rgbColor) < 0.5 ? 'white' : 'black';
      const borderRadius = '16px';
      return { backgroundColor, color, borderRadius };
    }
    return undefined;
  }

  addChip(event: MatChipInputEvent): void {
    const { chipInput, value = '' } = event;

    if (this.validators.some(validator => !validator(value))) {
      return;
    }

    if (chipInput?.inputElement) {
      chipInput.inputElement.value = '';
    }
    if (value) {
      const addedChips = value.split(',');
      this.chipInput.nativeElement.value = '';
      for (let chip of addedChips) {
        chip = chip.replace(/\s+/g, ' ').trim();
        if (this.strictSuggestions) {
          // look for exact match; or if a partial input is entered, pick the top suggestion
          const chipLower = chip.toLowerCase();
          const matchSuggestion = (this.suggestions ?? []).find(s => s.value.toLowerCase() === chipLower)
            ?? (this.suggestions ?? []).find(s => s.value.toLowerCase().includes(chipLower));
          if (matchSuggestion) {
            if (this.allowDuplicates) {
              this.chips = [...this.chips, matchSuggestion.value];
            } else {
              this.chips = Array.from(new Set([...this.chips, matchSuggestion.value]));
            }
          }
        } else {
          if (this.allowDuplicates) {
            this.chips = [...this.chips, chip];
          } else {
            this.chips = Array.from(new Set([...this.chips, chip]));
          }
        }
      }
      this.onChange(this.value);
      this.stateChanges.next();
      this.chipInput.nativeElement.blur();
      this.filterSuggestions();
      setTimeout(() => {
        this.chipInput.nativeElement.focus();
      });
    }
  }

  filterSuggestions(): void {
    this.filteredSuggestions = (this.suggestions ?? []).filter(suggestion => !this.value.includes(suggestion.value));
  }

  selectChip(event: MatAutocompleteSelectedEvent): void {
    this.addChip({
      input: this.chipInput.nativeElement,
      value: event.option.value,
      chipInput: null,
    });
  }

  removeChip(index: number): void {
    if (this.disabled) {
      return;
    }

    this.chips.splice(index, 1);
    this.onChange(this.value);
    this.stateChanges.next();
    this.filterSuggestions();
  }

  drop(event: CdkDragDrop<Set<string>>): void {
    if (this.disabled) {
      return;
    }
    const reorderedChips = Array.from(this.chips);
    const currentIndex = event.currentIndex;
    const previousIndex = event.previousIndex;
    moveItemInArray(reorderedChips, previousIndex, currentIndex);
    this.chips = reorderedChips;
    this.onChange(this.value);
    this.stateChanges.next();
  }

  paste(event: ClipboardEvent): void {
    event.preventDefault();
    if (this.disabled) {
      return;
    }

    this.addChip({
      input: this.chipInput.nativeElement,
      value: event.clipboardData.getData('Text'),
      chipInput: null,
    });
  }

  protected onMatChipInputTokenEnd(event: MatChipInputEvent): void {
    if (!this.isOnEnterAddingValue) {
      return;
    }

    this.addChip(event);
  }

  protected onValueChange(value: string): void {
    this.inputChange$.next(value)
  }
}
